import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Breadcrumbs from "../../component/breadcrumbs/breadcrumbs";
import { 
  Wrench, 
  MessageSquare, 
  Monitor, 
  Image, 
  BarChart3, 
  FileText, 
  Activity, 
  Fingerprint,
  Sparkles,
  Zap,
  Shield,
  Mic,
  Brain,
  ArrowRight
} from "lucide-react";
import "./portfolio.css";
import { useLanguage } from "../../i18n/LanguageContext";
import aiHairImageAnalysis from "../../assets/portfolio/AI Hair Image Analysis.webp";
import aiOcrFinancialBank from "../../assets/portfolio/AI OCR for Financial & Bank Document Data.webp";
import realTimeConversationalVoiceAgent from "../../assets/portfolio/Real-Time Conversational AI Voice Agent.webp";
import aiConsultationSummarization from "../../assets/portfolio/AI Consultation Summarization & Recommendation Assistant.webp";
import aiFingerprintProfiling from "../../assets/portfolio/AI-Based Fingerprint Analysis & Personality Profiling System.webp";
import smartAiVideoEditor from "../../assets/portfolio/Smart AI Video Editor.webp";

gsap.registerPlugin(ScrollTrigger);

const Portfolio = () => {
  const rootRef = useRef(null);
  const listElementRef = useRef(null);
  const mediaContainerRef = useRef(null);
  const yToRef = useRef(null);
  const { tr } = useLanguage();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  const isVideoUrl = (src) => {
    if (!src) return false;
    return /\.mp4(\?.*)?$/i.test(String(src));
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const portfolioItems = [
    // Domain: Computer Vision & Image/Video Analysis (5 projects)
    {
      title: "Smart AI Video Editor",
      category: "Computer Vision & Image/Video Analysis",
      description: "AI-powered tool that edits videos into structured, highlight-optimized content using prompts",
      image: smartAiVideoEditor,
      url: "https://cutandsnap.metagent.studio/auth",
      features: [
        {
          icon: Activity,
          title: "Workspace history management",
          description: "View, manage, and revisit previous projects and editing sessions"
        },
        {
          icon: Monitor,
          title: "Multi-video upload & batch processing",
          description: "Upload and process multiple videos simultaneously in a single workflow"
        },
        {
          icon: MessageSquare,
          title: "Prompt-based video editing",
          description: "Edit and structure videos using natural language prompts"
        },
        {
          icon: Brain,
          title: "Highlight & key moment detection with reasoning",
          description: "Automatically detect and prioritize important moments with AI-driven reasoning"
        },
        {
          icon: BarChart3,
          title: "Multiple generated versions (V1, V2, V3)",
          description: "Generate multiple alternative edits for easy comparison and selection"
        },
        {
          icon: FileText,
          title: "Context-aware revision from previous results",
          description: "Refine edits based on prior outputs without restarting from scratch"
        },
        {
          icon: Wrench,
          title: "Token-efficient prompt refinement",
          description: "Optimize prompt usage to reduce cost and improve iteration efficiency"
        }
      ]
    },
    {
      title: "AI Hair Image Analysis",
      category: "Computer Vision & Image/Video Analysis",
      description: "Revolutionary AI-powered system that analyzes hair images with precision, providing detailed insights on hair health, texture, and quality for beauty and wellness industries",
      image: aiHairImageAnalysis,
      url: null,
      features: [
        {
          icon: Image,
          title: "Advanced Image Recognition",
          description: "Leverage cutting-edge computer vision technology to analyze hair characteristics with exceptional precision, building trust through reliable and consistent results that users can depend on"
        },
        {
          icon: Sparkles,
          title: "Personalized Recommendations",
          description: "Generate tailored hair care recommendations based on comprehensive analysis, helping customers find the perfect products for their unique hair type"
        },
        {
          icon: Zap,
          title: "Real-time Processing",
          description: "Process and analyze hair images in seconds, delivering instant insights that enable quick decision-making for both professionals and consumers"
        }
      ]
    },
    {
      title: "AI OCR for Financial & Bank Document Data",
      category: "Knowledge, Reasoning, and Agents",
      description: "Intelligent document processing system that extracts, validates, and structures financial data from complex bank documents with enterprise-grade accuracy and security",
      image: aiOcrFinancialBank,
      url: "https://clario.infidea.dev/",
      features: [
        {
          icon: FileText,
          title: "Intelligent Data Extraction",
          description: "Automatically extract critical information from invoices, statements, and contracts with remarkable reliability, giving users confidence in every transaction and supporting multiple document formats and languages"
        },
        {
          icon: Shield,
          title: "Bank-Level Security",
          description: "Ensure complete data protection with encryption, access controls, and compliance with financial regulations including PCI-DSS and GDPR standards"
        },
        {
          icon: Zap,
          title: "Seamless Integration",
          description: "Integrate effortlessly with existing banking systems and workflows, reducing processing time from hours to minutes while eliminating manual errors"
        }
      ]
    },
    {
      title: "Real-Time Conversational AI Voice Agent",
      category: "Conversation AI",
      description: "Advanced AI-powered voice assistant that enables natural, real-time conversations with human-like understanding and responsiveness, transforming customer service and user interactions across industries",
      image: realTimeConversationalVoiceAgent,
      url: "https://ai-hair-doctor-juli.infidea.dev/",
      features: [
        {
          icon: Mic,
          title: "Natural Voice Interaction",
          description: "Engage in fluid, natural conversations with advanced speech recognition and synthesis technology that understands context, tone, and intent, creating a trustworthy and reliable communication experience users love"
        },
        {
          icon: Zap,
          title: "Real-Time Processing",
          description: "Deliver instant responses with ultra-low latency, enabling seamless conversations that feel natural and immediate, enhancing user experience significantly"
        },
        {
          icon: Brain,
          title: "Contextual Understanding",
          description: "Maintain conversation context across multiple turns, remembering previous interactions and adapting responses to provide personalized and relevant assistance"
        }
      ]
    },
    {
      title: "AI Consultation Summarization & Recommendation Assistant",
      category: "Knowledge, Reasoning, and Agents",
      description: "Intelligent AI system that automatically summarizes consultation sessions and generates personalized recommendations, helping healthcare professionals, consultants, and service providers deliver better outcomes",
      image: aiConsultationSummarization,
      url: null,
      features: [
        {
          icon: FileText,
          title: "Automated Summarization",
          description: "Transform lengthy consultation sessions into concise, structured summaries that capture key points, decisions, and action items reliably, giving professionals and clients peace of mind while saving hours of manual documentation"
        },
        {
          icon: Brain,
          title: "Intelligent Recommendations",
          description: "Generate personalized recommendations based on consultation content, patient history, and best practices, providing actionable insights that support informed decision-making"
        },
        {
          icon: Sparkles,
          title: "Multi-Domain Support",
          description: "Adapt to various consultation types including medical, legal, financial, and business consulting, with domain-specific knowledge and terminology understanding"
        }
      ]
    },
    {
      title: "AI-Based Fingerprint Analysis & Personality Profiling System",
      category: "Infrastructure & Product",
      description: "Innovative biometric analysis platform that combines fingerprint recognition with psychological profiling, offering unique insights for security and personal development applications",
      image: aiFingerprintProfiling,
      url: null,
      features: [
        {
          icon: Fingerprint,
          title: "Biometric Authentication",
          description: "Deliver secure, contactless identification with advanced fingerprint recognition technology that works across various devices and conditions"
        },
        {
          icon: Sparkles,
          title: "Personality Insights",
          description: "Provide fascinating personality analysis based on fingerprint patterns, offering valuable insights for personal development and self-awareness"
        },
        {
          icon: Shield,
          title: "Privacy-First Approach",
          description: "Maintain the highest standards of data privacy and security, ensuring all biometric data is encrypted and processed with user consent and transparency"
        }
      ]
    },
    
    
  ];

  useEffect(() => {
    const root = rootRef.current;
    const listElement = listElementRef.current;
    const mediaContainer = mediaContainerRef.current;

    if (!root || !listElement || !mediaContainer) return;

    const mediasUrl = portfolioItems.map(item => item.image);

    const handleMouseEnter = () => {
      mediaContainer.classList.add('on');
    };

    const handleMouseLeave = () => {
      mediaContainer.classList.remove('on');
      Array.from(mediaContainer.children).forEach(el => {
        const video = el.querySelector?.('video');
        if (video) video.pause();
        el.remove();
      });
    };

    listElement.addEventListener('mouseenter', handleMouseEnter);
    listElement.addEventListener('mouseleave', handleMouseLeave);

    // Set media container to center of viewport (fixed position)
    gsap.set(mediaContainer, { 
      top: '50%',
      yPercent: -50,
      y: 0
    });

    const rows = listElement.querySelectorAll('li');
    
    const createMedia = (index) => {
      // Clear previous media
      Array.from(mediaContainer.children).forEach(el => {
        const video = el.querySelector?.('video');
        if (video) video.pause();
        el.remove();
      });

      let div = document.createElement("div");
      const src = mediasUrl[index];
      const title = portfolioItems[index]?.title || "Portfolio media";

      let mediaEl;
      if (isVideoUrl(src)) {
        const video = document.createElement("video");
        video.src = src;
        video.muted = true;
        video.loop = true;
        video.autoplay = true;
        video.playsInline = true;
        video.preload = "metadata";
        video.setAttribute("aria-label", title);
        mediaEl = video;
      } else {
        const image = document.createElement("img");
        image.src = src;
        image.alt = title;
        mediaEl = image;
      }

      div.appendChild(mediaEl);
      mediaContainer.appendChild(div);

      // Set initial position
      gsap.set(div, { y: '-100%' });
      gsap.set(mediaEl, { y: '90%' });

      // Animate to visible
      gsap.to([div, mediaEl], {
        y: 0,
        duration: 0.6,
        ease: 'expo.inOut'
      });

      if (mediaContainer.children.length > 20) {
        mediaContainer.children[0].remove();
      }
    };

    rows.forEach((row, index) => {
      row.addEventListener('mouseenter', () => {
        createMedia(index);
      });
    });

    return () => {
      listElement.removeEventListener('mouseenter', handleMouseEnter);
      listElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <section className="mwg_effect030" ref={rootRef}>
      <Breadcrumbs />
      <motion.div
        className="portfolio-heading-banner"
        initial={{ opacity: 0, y: 20 }}
        {...(isMobile 
          ? { animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } }
          : { whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.5 }, viewport: { once: true } }
        )}
      >
        <motion.div
          className="heading-content"
          initial={{ opacity: 0, y: 20 }}
          {...(isMobile 
            ? { animate: { opacity: 1, y: 0 }, transition: { duration: 0.3, delay: 0.05 } }
            : { whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: 0.1 }, viewport: { once: true } }
          )}
        >
          <motion.div
            className="heading-line"
            initial={{ opacity: 0, y: 20 }}
            {...(isMobile 
              ? { animate: { opacity: 1, y: 0 }, transition: { duration: 0.3, delay: 0.1 } }
              : { whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: 0.2 }, viewport: { once: true } }
            )}
          >
            {tr("Where AI Meets", "Tempat AI Bertemu")}
          </motion.div>
          <motion.div
            className="heading-line"
            initial={{ opacity: 0, y: 20 }}
            {...(isMobile 
              ? { animate: { opacity: 1, y: 0 }, transition: { duration: 0.3, delay: 0.15 } }
              : { whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: 0.3 }, viewport: { once: true } }
            )}
          >
            {tr("Real Innovation", "Inovasi Nyata")} <span className="sparkle-emoji">✨</span>
          </motion.div>
        </motion.div>
      </motion.div>
      <ul className="portfolio-items-list">
        {portfolioItems.map((item, index) => {
          const isEven = index % 2 === 0; // Item ganjil (index 0, 2, 4...) = true, genap (1, 3, 5...) = false
          return (
            <li key={index} className={`portfolio-item ${isEven ? 'image-right' : 'image-left'}`}>
              <div className="portfolio-item-content">
                {/* Header Section: Title dan Description di atas (full width) */}
                <div className="portfolio-item-header">
                  <div className="portfolio-item-header-left">
                    <h2 className="portfolio-item-title">{item.title}</h2>
                    {/* <p className="portfolio-item-subtitle">{item.category}</p> */}
                  </div>
                  {item.url && (
                    <button 
                      className="portfolio-item-button"
                      onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
                    >
                      <span>{tr("View Details", "Lihat Detail")}</span>
                      <ArrowRight size={20} />
                    </button>
                  )}
                </div>
                <p className="portfolio-item-description">{item.description}</p>

                {/* Content Section: Foto dan Features sejajar di bawah */}
                <div className="portfolio-item-body">
                  {isEven ? (
                    <>
                      {/* Item ganjil: Features di kiri, Gambar di kanan */}
                      <div className="portfolio-item-features">
                        {item.features && item.features.map((feature, featureIndex) => {
                          const IconComponent = feature.icon;
                          return (
                            <div key={featureIndex} className="portfolio-feature">
                              <div className="portfolio-feature-icon">
                                <IconComponent size={24} />
                              </div>
                              <div className="portfolio-feature-content">
                                <h3 className="portfolio-feature-title">{feature.title}</h3>
                                <p className="portfolio-feature-description">{feature.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="portfolio-item-image-section">
                        <div className="portfolio-item-image-wrapper">
                          {isVideoUrl(item.image) ? (
                            <video
                              src={item.image}
                              className="portfolio-item-image"
                              // autoPlay
                              // muted
                              // loop
                              playsInline
                              controls
                              preload="metadata"
                              aria-label={item.title}
                            />
                          ) : (
                            <img src={item.image} alt={item.title} className="portfolio-item-image" />
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Item genap: Gambar di kiri, Features di kanan */}
                      <div className="portfolio-item-image-section">
                        <div className="portfolio-item-image-wrapper">
                          {isVideoUrl(item.image) ? (
                            <video
                              src={item.image}
                              className="portfolio-item-image"
                              // autoPlay
                              // muted
                              // loop
                              playsInline
                              controls
                              preload="metadata"
                              aria-label={item.title}
                            />
                          ) : (
                            <img src={item.image} alt={item.title} className="portfolio-item-image" />
                          )}
                        </div>
                      </div>
                      <div className="portfolio-item-features">
                        {item.features && item.features.map((feature, featureIndex) => {
                          const IconComponent = feature.icon;
                          return (
                            <div key={featureIndex} className="portfolio-feature">
                              <div className="portfolio-feature-icon">
                                <IconComponent size={24} />
                              </div>
                              <div className="portfolio-feature-content">
                                <h3 className="portfolio-feature-title">{feature.title}</h3>
                                <p className="portfolio-feature-description">{feature.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
          </li>
          );
        })}
      </ul>

      {/* Hidden images for preloading */}
      <div className="medias hidden" aria-hidden="true">
        {portfolioItems.map((item, index) =>
          isVideoUrl(item.image) ? (
            <video
              key={index}
              src={item.image}
              muted
              playsInline
              preload="metadata"
              aria-label={item.title}
            />
          ) : (
            <img key={index} src={item.image} alt={item.title} />
          )
        )}
      </div>

      <div className="media-container" ref={mediaContainerRef}></div>
    </section>
  );
};

export default Portfolio;



